/**
 * Google Ads conversions (NOT GA4) — self-registering.
 *
 * Loads `gtag.js` for the AW-id on first PAGE_VIEW with consent. Fires a
 * `conversion` event on each ECOM_LINK_CLICK with consent, and per mapped
 * session-value milestone via `value_pixel` (cent → conversion label).
 */

import { warn } from '../../util/log';
import type { GoogleAdsPixel } from '../../core/config/types';
import { on, SdkEvent } from '../../core/events/bus';
import type { PixelRegister } from './types';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const tagsLoaded = new Set<string>();

function ensureGtag(awId: string): void {
  if (typeof window === 'undefined') return;
  if (!window.dataLayer) window.dataLayer = [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag(...args: unknown[]): void {
      window.dataLayer!.push(args);
    };
  }
  if (tagsLoaded.has(awId)) return;
  tagsLoaded.add(awId);
  try {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(awId)}`;
    (document.head || document.documentElement).appendChild(s);
    window.gtag!('js', new Date());
    window.gtag!('config', awId, { send_page_view: false });
  } catch (err) {
    warn('google ads gtag inject failed', err);
  }
}

export const registerGoogleAds: PixelRegister<GoogleAdsPixel> = (pixel, context) => {
  on(SdkEvent.PAGE_VIEW, () => {
    if (context.getConsent() !== 'granted') return;
    ensureGtag(pixel.pixel_id);
  });

  // Conversions are inherently scoped: `send_to` targets this AW id only.
  on(SdkEvent.ECOM_CLICKOUT, () => {
    if (context.getConsent() !== 'granted') return;
    if (!pixel.pixel_tag_id) return;
    try {
      ensureGtag(pixel.pixel_id);
      window.gtag!('event', 'conversion', {
        send_to: `${pixel.pixel_id}/${pixel.pixel_tag_id}`,
      });
    } catch (err) {
      warn('google ads conversion failed', err);
    }
  });

  // mapped milestones only (yoto parity)
  on(SdkEvent.SESSION_BID_CENT_INCREMENT, (cents) => {
    if (context.getConsent() !== 'granted') return;
    const label = pixel.value_pixel?.[String(cents)];
    if (!label) return;
    try {
      ensureGtag(pixel.pixel_id);
      window.gtag!('event', 'conversion', { send_to: `${pixel.pixel_id}/${label}` });
    } catch (err) {
      warn('google ads cent conversion failed', err);
    }
  });
};

export function _resetGoogleAdsForTests(): void {
  tagsLoaded.clear();
}
